/**
 * Policy Navigation Component
 * Works with links configured in Liquid/Admin settings
 */
export class PolicyNavigation extends HTMLElement {
  /** @type {HTMLUListElement | null} */
  #navList = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLAnchorElement | null} */
  #activeLink = null;

  #previousScrollY = 0;
  #shouldRestoreScroll = false;

  connectedCallback() {
    this.#navList = this.querySelector('[data-policy-nav-list]');
    this.#toggle = this.querySelector('[data-policy-nav-toggle]');

    if (!this.#navList) return;

    this.#setToggle();
    this.#setLinkHandling();
    this.#setActiveByCurrentUrl();
  }

  /**
   * Setup mobile toggle
   */
  #setToggle = () => {
    this.#toggle?.addEventListener('click', () => {
      const isExpanded = this.#toggle?.getAttribute('aria-expanded') === 'true';

      if (!isExpanded) {
        this.#previousScrollY = window.scrollY;
        this.#shouldRestoreScroll = true;
        this.#toggle?.setAttribute('aria-expanded', 'true');
        this.#navList?.classList.add('is-open');
        this.#disableScroll();
      } else {
        this.#closeMenu();
        this.#enableScroll();
      }
    });
  };

  /**
   * Close menu after nav click on mobile
   */
  #setLinkHandling = () => {
    this.#navList?.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('.policy-nav__link');
      if (!link) return;

      if (window.innerWidth < 750) {
        this.#closeMenu();
        this.#enableScroll();
      }
    });
  };

  /**
   * Highlight link matching current page URL
   */
  #setActiveByCurrentUrl = () => {
    const links = this.#navList?.querySelectorAll('.policy-nav__link');
    if (!links?.length) return;

    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';

    const matchedLink = Array.from(links).find((link) => {
      const href = link.getAttribute('href');
      if (!href) return false;
      const linkPath = (href.split('?')[0]?.split('#')[0] ?? '').replace(/\/+$/, '') || '/';
      return linkPath === currentPath;
    }) ?? links[0] ?? null;

    if (matchedLink) {
      this.#setActiveLink(/** @type {HTMLAnchorElement} */ (matchedLink));
    }
  };

  #disableScroll = () => {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
  };

  #enableScroll = () => {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (this.#shouldRestoreScroll) {
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    this.#shouldRestoreScroll = false;
  };

  /**
   * Close mobile menu
   */
  #closeMenu = () => {
    this.#toggle?.setAttribute('aria-expanded', 'false');
    this.#navList?.classList.remove('is-open');
  };

  /**
   * Set active link styling
   * @param {HTMLAnchorElement | null} link
   */
  #setActiveLink = (link) => {
    this.#activeLink?.classList.remove('is-active');
    link?.classList.add('is-active');
    this.#activeLink = link;
  };
}

if (!customElements.get('policy-navigation')) {
  customElements.define('policy-navigation', PolicyNavigation);
}
